import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { CountUp, IconBadge } from '@/components/atoms';
import { type CompanyFact } from '@/hooks/useCompanyStory';

/**
 * One fact in the rail beside the story, drawn as a term-and-definition pair.
 *
 * The figure is set in display type, which is what makes this the treatment
 * for a band with a row to give it: three of these across a column read as a
 * rail of landmarks. Stacked in a narrow column they read as a comparison
 * nobody is making, so a compact band uses `StoryFactCard` instead.
 *
 * The term comes first in the DOM and the definition after it, which is the
 * order a description list requires; the old version emitted `dd` before `dt`
 * and put them back in visual order with `order: 2`, so the markup said the
 * label defined the figure. Here the figure is simply set in display type and
 * the label above it stays small — the visual hierarchy is type, not order.
 *
 * A year counts up because it is a number the eye can follow; the city does
 * not, because counting to a word is nonsense.
 */
export function StoryFact({ fact }: { fact: CompanyFact }) {
  return (
    <Box
      sx={{
        pl: { xs: 5, md: 6 },
        borderLeft: 2,
        borderColor: 'divider',
        transition: 'border-color 250ms ease',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Stack spacing={2}>
        <IconBadge name={fact.icon} size={36} shape="circle" tone="tint" />
        <Typography component="dt" variant="overline" color="text.secondary">
          {fact.label}
        </Typography>
        <Typography
          component="dd"
          sx={{
            m: 0,
            fontFamily: 'h1.fontFamily',
            fontWeight: 800,
            fontSize: { xs: '2rem', md: '2.5rem' },
            lineHeight: 1.05,
            letterSpacing: '-0.02em',
          }}
        >
          {fact.value === null ? fact.display : <CountUp value={fact.value} grouped={false} />}
        </Typography>
        <Typography component="dd" variant="body2" color="text.secondary" sx={{ m: 0 }}>
          {fact.note}
        </Typography>
      </Stack>
    </Box>
  );
}
